/** Official 1:1 app mark (rasterized from the CMS ON45D favicon). */
export const APP_MARK = "/icons/icon-192.png";
export const APP_MARK_APPLE = "/icons/apple-touch-icon.png";
export const APP_MARK_512 = "/icons/icon-512.png";

/** Prefer vector favicon for tabs / UI when CMS has none yet. */
export const DEFAULT_FAVICON = "/favicon.svg";

type Listener = (url: string) => void;

let currentAppIcon = DEFAULT_FAVICON;
const listeners = new Set<Listener>();

function iconType(href: string) {
  const clean = href.split("?")[0].toLowerCase();
  if (clean.endsWith(".svg")) return "image/svg+xml";
  if (clean.endsWith(".png")) return "image/png";
  if (clean.endsWith(".webp")) return "image/webp";
  if (clean.endsWith(".jpg") || clean.endsWith(".jpeg")) return "image/jpeg";
  if (clean.endsWith(".ico")) return "image/x-icon";
  return "image/png";
}

function notify(url: string) {
  listeners.forEach((listener) => listener(url));
}

export function getAppIconUrl() {
  return currentAppIcon;
}

export function subscribeAppIcon(listener: Listener) {
  listeners.add(listener);
  listener(currentAppIcon);
  return () => {
    listeners.delete(listener);
  };
}

function upsertLink(rel: string, attrs: Record<string, string>) {
  let link = document.head.querySelector(`link[data-app-icon="${rel}"]`) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("data-app-icon", rel);
    document.head.appendChild(link);
  }
  link.rel = rel;
  Object.entries(attrs).forEach(([key, value]) => {
    link!.setAttribute(key, value);
  });
}

function syncDocumentIcons(url: string) {
  // Replace stock favicon / apple tags. Never touch rel="manifest" (required for installability).
  document
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((link) => {
      if (!(link as HTMLElement).hasAttribute("data-app-icon")) {
        link.remove();
      }
    });

  const type = iconType(url);
  upsertLink("icon", { type, href: url, sizes: "any" });
  upsertLink("apple-touch-icon", {
    href:
      url === DEFAULT_FAVICON ||
      url === APP_MARK ||
      url.endsWith("/favicon.svg") ||
      url === "/favicon.svg"
        ? APP_MARK_APPLE
        : url,
    sizes: "180x180",
  });
}

/**
 * Apply the studio favicon to the browser tab / apple touch icon and notify UI
 * (sidebar mark, BrandLogo). PWA install icons stay on the static webmanifest
 * so Chrome/Safari installability is not broken by blob: manifests.
 */
export function applyFavicon(href: string) {
  const url = (href || "").trim() || DEFAULT_FAVICON;
  currentAppIcon = url;
  if (typeof document === "undefined") {
    notify(url);
    return;
  }
  syncDocumentIcons(url);
  notify(url);
}
